import { normalizePremiumEquivalentCategory } from './productRules';
import { normalizeDurationKey } from './helpers';

/**
 * Normalizes validity strings to match rule keys
 * @param {string} validity - The input validity string (e.g., "1 Month")
 * @returns {string} - Normalized key (e.g., "monthly")
 */
export const normalizeValidity = (validity) => {
    if (!validity) return '';
    const v = validity.toLowerCase().trim();
    const normalizedDuration = normalizeDurationKey(v);
    if (normalizedDuration) return normalizedDuration;
    if (v.includes('1 month') || v === 'monthly') return 'monthly';
    if (v.includes('3 month') || v === 'quarterly' || v === 'quaterly') return 'quaterly'; // Supports both spellings + user preference
    if (v.includes('6 month') || v === 'semiannually') return 'semiannually';
    if (v.includes('1 year') || v === 'yearly' || v.includes('12 month')) return 'yearly';
    if (v === 'lifetime') return 'lifetime';
    return v;
};

/**
 * Calculates the discounted price based on store discount rules.
 * Rules format: { "premium-monthly": "5%", "bundles-monthly,quaterly,yearly": "10%" }
 * 
 * @param {object} product - The product object
 * @param {number} price - The current price of the product
 * @param {string} validity - The selected validity (e.g., "1 Month")
 * @param {object} storeDiscounts - The discount rules from Redux/DB
 * @returns {object} { finalPrice, originalPrice, discountPercentage, isDiscounted }
 */
const normalizeCategory = (cat) => {
    if (!cat) return '';
    const c = cat.toLowerCase().trim();
    if (c === 'bundles') return 'bundle'; // Handle plural rule vs singular data
    return normalizePremiumEquivalentCategory(c);
};

export const calculateDiscountedPrice = (product, price, validity, storeDiscounts) => {
    // console.log('calculateDiscountedPrice input:', { product: product?.name, price, validity, storeDiscounts });
    if (!price || !storeDiscounts || Object.keys(storeDiscounts).length === 0) {
        return {
            finalPrice: price,
            originalPrice: price,
            discountPercentage: 0,
            isDiscounted: false
        };
    }

    const productCategory = normalizeCategory(product.category);
    const productName = (product.name || '').trim().toLowerCase();
    const normalizedValid = normalizeValidity(validity);
    let maxProductDiscount = 0;
    let maxCategoryDiscount = 0;

    // console.log(`[PriceUtils] Checking ${product.name} (Cat: ${productCategory}, Val: ${normalizedValid})`);

    // Ensure storeDiscounts is an object
    if (!storeDiscounts || typeof storeDiscounts !== 'object') return { finalPrice: price, originalPrice: price, discountPercentage: 0, isDiscounted: false };

    // Iterate through all discount rules
    Object.entries(storeDiscounts).forEach(([ruleKey, discountValue]) => {
        let percentage = 0;
        if (typeof discountValue === 'string') {
            percentage = parseFloat(discountValue.replace('%', ''));
        } else {
            percentage = parseFloat(discountValue);
        }

        if (isNaN(percentage) || percentage <= 0) return;

        // Split rule key into parts (e.g., "bundles-monthly,quaterly,yearly")
        const parts = ruleKey.split(',').map(p => p.trim().toLowerCase());

        let categoryContext = null;
        let productContext = null;
        let ruleMatched = false;
        let matchType = null; // 'product' or 'category'

        const validities = ['monthly', 'quaterly', 'quarterly', 'yearly', 'lifetime', 'semiannually'];
        const customValidityPattern = /(?:^|-)(\d{1,4}d)$/;

        for (const part of parts) {
            // Check for explicit "name-validity" or "cat-validity"
            let matchedSuffix = null;
            for (const v of validities) {
                if (part.endsWith('-' + v)) {
                    matchedSuffix = v;
                    break;
                }
            }
            if (!matchedSuffix) {
                const customMatch = part.match(customValidityPattern);
                if (customMatch) matchedSuffix = customMatch[1];
            }

            if (matchedSuffix) {
                // Format: entity-validity (e.g. "fish of fortune-monthly" or "premium-monthly")
                // Extract entity by removing the suffix
                const entity = part.slice(0, -(matchedSuffix.length + 1)).trim();

                // Check matches
                const isCatMatch = normalizeCategory(entity) === productCategory;
                const isNameMatch = entity === productName;

                if ((isCatMatch || isNameMatch)) {
                    // Validate validity
                    if (matchedSuffix === normalizedValid || normalizeValidity(matchedSuffix) === normalizedValid) {
                        ruleMatched = true;
                        matchType = isNameMatch ? 'product' : 'category';
                        break;
                    }
                    // Set context for future parts
                    if (isCatMatch) categoryContext = productCategory;
                    if (isNameMatch) productContext = productName;
                }
            } else if (validities.includes(part)) {
                // Standalone validity keyword (e.g., "monthly" in "premium, monthly, yearly")
                const isProdContext = productContext === productName;
                const isCatContext = categoryContext === productCategory;

                if ((isProdContext || isCatContext) && (part === normalizedValid || normalizeValidity(part) === normalizedValid)) {
                    ruleMatched = true;
                    matchType = isProdContext ? 'product' : 'category';
                    break;
                }
            } else {
                // Likely a Category or Product Name (e.g. "premium" or "fish of fortune")
                // Set contexts
                if (normalizeCategory(part) === productCategory) categoryContext = productCategory;
                if (part === productName) productContext = productName;
            }
        }

        if (ruleMatched) {
            if (matchType === 'product') {
                maxProductDiscount = Math.max(maxProductDiscount, percentage);
            } else {
                maxCategoryDiscount = Math.max(maxCategoryDiscount, percentage);
            }
        }
    });

    // Prioritize product specific discount if exists, otherwise use category discount
    let bestDiscount = maxProductDiscount > 0 ? maxProductDiscount : maxCategoryDiscount;

    if (bestDiscount > 0) {
        const discountAmount = (price * bestDiscount) / 100;
        const finalPrice = price - discountAmount;
        // console.log(`Discount Applied! ${product.name}: ${price} -> ${finalPrice} (-${bestDiscount}%)`);
        return {
            finalPrice: finalPrice,
            originalPrice: price,
            discountPercentage: bestDiscount,
            isDiscounted: true
        };
    }

    return {
        finalPrice: price,
        originalPrice: price,
        discountPercentage: 0,
        isDiscounted: false
    };
};
